import useShippingBoConnectionRedirect from "@babackapp/private-api-client/client/use-shipping-bo-integration-connection-redirect";
import useShippingBoDisconnect from "@babackapp/private-api-client/client/use-shipping-bo-integration-disconnect";
import useUpdateShippingboIntegration from "@babackapp/private-api-client/client/use-update-shippingbo-integration";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { CheckCircleIcon } from "lucide-react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import Switch from "@/components-legacy/switch";
import IntegrationPreview from "./integration-preview";
import {useListShippingboHooks} from "@babackapp/private-api-client/client/use-list-shippingbo-hooks";
import {useResetShippingboHooks} from "@babackapp/private-api-client/client/use-reset-shippingbo-hooks";

function ShippingBoIntegration() {
	const t = useTranslation();

	const { data: shop } = useCurrentShopSuspense();
	const isConnected = !!shop.integrations.shippingBo.accessToken;
	const connectRedirect = useShippingBoConnectionRedirect(shop);
    const installedHooks = useListShippingboHooks({shopId: shop.id, enabled: !!shop.integrations.shippingBo.accessToken});
    const resetHooks = useResetShippingboHooks(shop);
	const disconnect = useShippingBoDisconnect(shop);
	const updateIntegration = useUpdateShippingboIntegration(shop);
	const isWildReturnsEnabled = !!shop.integrations.shippingBo.isWildReturnsEnabled;
	const isReturnClosingEnabled =
		shop.integrations.shippingBo.isReturnClosingEnabled ?? true;

	if (!isConnected) {
		return (
			<Button onClick={() => connectRedirect.mutateAsync()}>
				{t("settings.integrations.thirdpart.shippingBo.connect")}
			</Button>
		);
	}

	return (
		<>
			<div className="rounded-md bg-green-50 p-4 w-full">
				<div className="flex">
					<div className="flex-shrink-0">
						<CheckCircleIcon
							className="h-5 w-5 text-green-400"
							aria-hidden="true"
						/>
					</div>
					<div className="ml-3">
						<h3 className="text-sm font-medium text-green-800">
							{t(
								"settings.integrations.thirdpart.shippingBo.integrationCompleted",
							)}
						</h3>
						<div className="mt-2 text-sm text-green-700">
							<p>
								{t("settings.integrations.thirdpart.shippingBo.successMessage")}
							</p>
						</div>
					</div>
				</div>
			</div>

			<div className="flex items-center justify-between rounded-md border p-4">
				<div className="space-y-0.5">
					<p className="text-sm font-medium">
						{t("settings.integrations.thirdpart.shippingBo.wildReturnsLabel")}
					</p>
					<p className="text-sm text-muted-foreground">
						{t("settings.integrations.thirdpart.shippingBo.wildReturnsDescription")}
					</p>
				</div>
				<Switch
					value={isWildReturnsEnabled}
					onChange={(checked) =>
						updateIntegration.mutateAsync({ isWildReturnsEnabled: checked })
					}
				/>
			</div>

			<div className="flex items-center justify-between rounded-md border p-4">
				<div className="space-y-0.5">
					<p className="text-sm font-medium">
						{t("settings.integrations.thirdpart.shippingBo.returnClosingLabel")}
					</p>
					<p className="text-sm text-muted-foreground">
						{t("settings.integrations.thirdpart.shippingBo.returnClosingDescription")}
					</p>
				</div>
				<Switch
					value={isReturnClosingEnabled}
					onChange={(checked) =>
						updateIntegration.mutateAsync({ isReturnClosingEnabled: checked })
					}
				/>
			</div>

            {installedHooks.isFetched ?
            installedHooks?.data?.map(item => {
                return (
                    <div key={item.id} className="flex flex-col p-2 border rounded text-sm break-all">
                        <p>Id: {item.id}</p>
                        <p>Event: {item.object_class}</p>
                        <p>URL: {item.endpoint_url}</p>
                    </div>
                )
            }) : null}

            {/* Disconnect button */}
			<div className="flex justify-between">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => resetHooks.mutateAsync()}
              >
                {t("misc.refresh")}
              </Button>
				<Button
					variant="destructive"
					size="sm"
					onClick={() => disconnect.mutateAsync()}
				>
					{t("misc.disconnect")}
				</Button>
			</div>
		</>
	);
}

export function ShippingBoIntegrationPreview() {
	const t = useTranslation();
	const { data: shop } = useCurrentShopSuspense();

	if (!shop || !shop.plan.shippingBo) return null;

	return (
		<IntegrationPreview
			title="ShippingBo"
			description={t("settings.integrations.thirdpart.shippingBo.description")}
			logo="/integrations/shippingbo.png"
			seeMoreUrl="https://shippingbo.com/"
            onActivationUpdate={() => {}}
            activated={shop.integrations.shippingBo.activated}
		>
			<ShippingBoIntegration />
		</IntegrationPreview>
	);
}
